import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { AxiosResponse } from 'axios';
import { stats } from '@fiverr-private/obs';
import { BriefClassificationResponse, classifyBrief, getClassification } from '../../api';
import { reportTimeMetric } from '../../monitoring';
import { retryWrapper } from '../../utils';
import { METRICS_PREFIX, Step } from '../../constants';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { logger } from '../../utils/logger';
import { checkClassificationResponseIsValid, handleClassificationError } from './utils';
import { QUERY_RETRY_DELAY_MS } from './constants';
import { useIntentClassification } from './useIntentClassification';

const CLASSIFY_LEAF_CATEGORY_TIMEOUT_MS = 12 * 1000;
const requestType = 'classify_user_input';

export const useClassifyLeafCategory = () => {
    const queryClient = useQueryClient();
    const { setStep, setBriefClassification, contextId } = useBriefCreationContext();
    const { isLoading: isClassifyingIntent } = useIntentClassification();
    const [isLoading, setIsLoading] = useState(false);

    const handleLeafCategoryClassificationResult = ({
        classificationResponse,
        isAborted,
        userInput,
    }: {
        classificationResponse: AxiosResponse<BriefClassificationResponse>;
        isAborted: boolean;
        userInput: string;
    }) => {
        if (
            !checkClassificationResponseIsValid({
                classificationResponse,
                isAborted,
                requestType,
            })
        ) {
            return;
        }

        const { data: classification } = classificationResponse;

        const numberOfLeafClassifications = classification?.leaf_classification?.length;
        if (!numberOfLeafClassifications) {
            logger.warn(new Error('Failed while classifying user input to category - Rephrase step will be shown'), {
                userInput,
            });

            stats.count(`${METRICS_PREFIX}.${requestType}`, numberOfLeafClassifications === 0 ? 'rephrase' : 'error');
            setStep(numberOfLeafClassifications === 0 ? Step.Rephrase : Step.Error);
            return;
        }

        setBriefClassification(classification);

        const [firstLeafClassification] = classification.leaf_classification;

        stats.count(`${METRICS_PREFIX}.${requestType}`, 'success');

        return {
            ...classification,
            leaf_classification: [firstLeafClassification],
        };
    };

    const classifyLeafCategory = async ({
        userInput,
        abortController,
    }: {
        userInput: string;
        abortController: AbortController;
    }): Promise<BriefClassificationResponse | undefined> => {
        const requestType = 'classify_user_input';
        const classificationStartTime = Date.now();
        return retryWrapper<BriefClassificationResponse | undefined>({
            retries: 2,
            metric: requestType,
            abortController,
            wrappedFn: async () => {
                try {
                    setIsLoading(true);
                    const {
                        data: { poll_id: classificationPollingId },
                    } = await queryClient.fetchQuery({
                        queryKey: [requestType, userInput],
                        queryFn: () =>
                            classifyBrief({
                                userInput,
                                contextId,
                                extractors: [],
                                abortController,
                            }),
                        cacheTime: 0,
                        retry: 2,
                    });

                    if (!classificationPollingId) {
                        handleClassificationError({ requestType, errorMessage: 'Failed to POST classifyUserInput' });
                        return;
                    }

                    if (!userInput) {
                        return;
                    }

                    const result = await queryClient.fetchQuery({
                        queryKey: [requestType, classificationPollingId],
                        queryFn: () =>
                            getClassification({
                                pollingId: classificationPollingId,
                                inspectedField: 'leaf_classification',
                                abortController,
                            }),
                        retry: CLASSIFY_LEAF_CATEGORY_TIMEOUT_MS / QUERY_RETRY_DELAY_MS,
                        retryDelay: QUERY_RETRY_DELAY_MS,
                    });

                    return handleLeafCategoryClassificationResult({
                        classificationResponse: result,
                        isAborted: abortController.signal.aborted,
                        userInput,
                    });
                } catch (error: any) {
                    handleClassificationError({
                        requestType,
                        errorMessage: error.message,
                        isTimeout: error.message === 'No data',
                        isAborted: abortController.signal.aborted,
                    });
                    throw error;
                } finally {
                    setIsLoading(false);
                    reportTimeMetric({
                        metricName: requestType,
                        time: Date.now() - classificationStartTime,
                        timeout: CLASSIFY_LEAF_CATEGORY_TIMEOUT_MS,
                    });
                }
            },
        });
    };

    return {
        classifyLeafCategory,
        isLoading: isClassifyingIntent || isLoading,
    };
};
